/**
 * Pure builder for the `context.client` snapshot. App state is passed in by the
 * caller (hooks live in the panel); this module reads only browser globals.
 */
import { getConsoleLog } from './consoleRing';
import { getApiLog } from './apiRing';
import type { ClientContext, ClientContextInput } from './types';

interface NavigatorExtras {
  userAgentData?: { platform?: string };
  connection?: { effectiveType?: string };
  deviceMemory?: number;
}

export interface BrowserEnv {
  userAgent: string | null;
  platform: string | null;
  language: string | null;
  connectionType: string | null;
  deviceMemory: number | null;
  cores: number | null;
  viewport: { w: number; h: number } | null;
  screen: { w: number; h: number } | null;
  dpr: number | null;
  timezone: string | null;
  route: string;
}

export function browserEnv(): BrowserEnv {
  const nav: (Navigator & NavigatorExtras) | undefined =
    typeof navigator === 'undefined' ? undefined : navigator;
  const win: Window | undefined = globalThis.window;
  let timezone: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    timezone = null;
  }
  return {
    userAgent: nav?.userAgent ?? null,
    platform: nav?.userAgentData?.platform ?? nav?.platform ?? null,
    language: nav?.language ?? null,
    connectionType: nav?.connection?.effectiveType ?? null,
    deviceMemory: typeof nav?.deviceMemory === 'number' ? nav.deviceMemory : null,
    cores: typeof nav?.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null,
    viewport: win ? { w: win.innerWidth, h: win.innerHeight } : null,
    screen: win?.screen ? { w: win.screen.width, h: win.screen.height } : null,
    dpr: typeof win?.devicePixelRatio === 'number' ? win.devicePixelRatio : null,
    timezone,
    route: win ? win.location.pathname : '',
  };
}

/** Line terminators: `.` in a regex does not cross them, so neither does the Safari check below. */
const LINE_BREAK = /[\n\r\u2028\u2029]/;

/**
 * Safari reports its version as `Version/NN … Safari/…` on the same line. Checked as two linear
 * steps rather than `/Version\/(\d+).*Safari/`, whose `\d+` and `.*` overlap and backtrack
 * quadratically on a long digit run with no `Safari` after it.
 */
function safariVersion(ua: string): string | null {
  for (const line of ua.split(LINE_BREAK)) {
    const m = /Version\/(\d+)/.exec(line);
    if (m?.[1] && line.includes('Safari', m.index + m[0].length)) return m[1];
  }
  return null;
}

export function parseBrowser(ua: string | null): { name: string; version: string } | null {
  if (!ua) return null;
  const rules: Array<[string, RegExp]> = [
    ['Edge', /Edg\/(\d+)/],
    ['Opera', /OPR\/(\d+)/],
    ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
  ];
  for (const [name, re] of rules) {
    const m = re.exec(ua);
    if (m?.[1]) return { name, version: m[1] };
  }
  const safari = safariVersion(ua);
  return safari ? { name: 'Safari', version: safari } : null;
}

type ScalarValue = string | number | boolean | null;

function scalarOptions(v: unknown): Record<string, ScalarValue> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out: Record<string, ScalarValue> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>).slice(0, 50)) {
    if (val === null || ['string', 'number', 'boolean'].includes(typeof val)) {
      out[k] = val as ScalarValue;
    }
  }
  return out;
}

export function buildClientContext(
  input: ClientContextInput,
  env: BrowserEnv = browserEnv(),
): ClientContext {
  const version = input.config.version;
  return {
    user: input.user ? { displayName: input.user.displayName, userId: input.user.userId } : null,
    activeSite: input.activeSite,
    clientOptions: scalarOptions(input.clientOptions),
    gameDay: input.gameDay,
    tab: input.tab,
    tool: input.tool,
    breadcrumb: input.breadcrumb,
    theme: input.theme,
    mode: input.mode,
    environment: input.config.environment,
    route: env.route.split('?')[0]?.split('#')[0] ?? '',
    browser: parseBrowser(env.userAgent),
    os: env.platform,
    viewport: env.viewport,
    screen: env.screen,
    dpr: env.dpr,
    timezone: env.timezone,
    locale: env.language,
    connectionType: env.connectionType,
    deviceMemory: env.deviceMemory,
    cores: env.cores,
    build: {
      version,
      sha: version && version !== '0.0.0' ? version : null,
      buildTimestamp: input.config.buildTimestamp,
    },
    consoleLog: getConsoleLog(),
    apiLog: getApiLog(),
  };
}
